
import { defineComponent, onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { useForm } from "vee-validate";
import { Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";

import { StepperComponent } from "@/assets/ts/components/_StepperComponent";
import { hideModal } from "@/core/helpers/dom";
import { getIllustrationsPath } from "@/core/helpers/assets";
import ApiService from "@/core/services/ApiService";
import { Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";

interface Customer {
  customer_id: string;
  status: string;
  alias: string;
  group_id: string;
  street1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Billing {
  admin_id: string;
  brand: string;
  exp_month: string;
  exp_year: string;
  card_number: string;
}

interface Accounting {
  billing_type: string;
  card_type: string;
  account_alias: string;
  account_owner: string;
  account_fname: string;
  account_lname: string;
  note_comments: string;
}

interface CreateBilling extends Customer, Billing, Accounting {}

export default defineComponent({
  name: "create-billing-modal",
  components: {
    Field,
    ErrorMessage,
  },
  setup() {
    const store = useStore();
    const _stepperObj = ref<StepperComponent | null>(null);
    const createBillingRef = ref<HTMLElement | null>(null);
    const createBillingModalRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const submitButton = ref<HTMLButtonElement | null>(null);

    const formData = ref<CreateBilling>({
      customer_id: "",
      status: "",
      alias: "",
      group_id: "",
      street1: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      admin_id: "",
      brand: "",
      exp_month: "",
      exp_year: "",
      card_number: "",
      billing_type: "",
      card_type: "",
      account_alias: "",
      account_owner: "",
      account_fname: "",
      account_lname: "",
      note_comments: "",
    });

    onMounted(() => {
      _stepperObj.value = StepperComponent.createInsance(
        createBillingRef.value as HTMLElement
      );
    });

    const createBillingSchema = [
      Yup.object({
        alias: Yup.string().required().label("Company Alias"),
        street1: Yup.string().required().label("Street"),
        city: Yup.string().required().label("City"),
        state: Yup.string().required().label("State"),
        zip: Yup.string().required().label("Zip"),
        country: Yup.string().required().label("Country"),
      }),
      Yup.object({
        brand: Yup.string().required().label("Brand"),
        exp_month: Yup.string().required().label("Exp Month"),
        exp_year: Yup.string().required().label("Exp Year"),
        card_number: Yup.string().required().label("Card Number"),
      }),
      Yup.object({
        billing_type: Yup.string().required().label("Billing Type"),
        card_type: Yup.string().required().label("Card Type"),
        account_alias: Yup.string().required().label("Alias"),
        account_owner: Yup.string().required().label("Account Owner"),
        account_fname: Yup.string().required().label("Account First Name"),
        account_lname: Yup.string().required().label("Account Last Name"),
      }),
    ];

    // extracts the individual step schema
    const currentSchema = computed(() => {
      return createBillingSchema[currentStepIndex.value];
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    const { resetForm, handleSubmit } = useForm<
      Customer | Billing | Accounting
    >({
      validationSchema: currentSchema,
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const handleStep = handleSubmit((values) => {
      formData.value = {
        ...formData.value,
        ...values,
      };

      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const formSubmit = async () => {
      // eslint-disable-next-line
      submitButton.value!.disabled = true;
      // Activate indicator
      submitButton.value?.setAttribute("data-kt-indicator", "on");

      // eslint-disable-next-line
      const payload: any = {
        customer: {
          customer_id: store.getters.currentUser.data.customer_id,
          status: formData.value.status,
          alias: formData.value.alias,
          group_id: store.getters.currentUser.data.companygroupid,
          street1: formData.value.street1,
          city: formData.value.city,
          state: formData.value.state,
          zip: formData.value.zip,
          country: formData.value.country,
        },
        billing: {
          admin_id: store.getters.currentUser.data.adminid,
          brand: formData.value.brand,
          exp_month: formData.value.exp_month,
          exp_year: formData.value.exp_year + (new Date().getFullYear() - 1),
          card_number: formData.value.card_number,
        },
        accounting: {
          billing_type: formData.value.billing_type,
          card_type: formData.value.card_type,
          alias: formData.value.account_alias,
          account_owner: formData.value.account_owner,
          account_fname: formData.value.account_fname,
          account_lname: formData.value.account_lname,
          note_comments: formData.value.note_comments,
        },
      };

      try {
        const res = await ApiService.post(
          "customers/accounts?action=create_billing",
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully created the billing info!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(function () {
            store.dispatch(Actions.VERIFY_AUTH, {
              token: JwtService.getToken(),
            });
            hideModal(createBillingModalRef.value);
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButton.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButton.value!.disabled = false;
    };

    resetForm({
      values: {
        ...formData.value,
      },
    });

    return {
      handleStep,
      formSubmit,
      previousStep,
      createBillingRef,
      currentStepIndex,
      totalSteps,
      createBillingModalRef,
      getIllustrationsPath,
      submitButton,
    };
  },
});
